import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin } from "lucide-react";

const LangToggle = ({ lang, setLang, dark }) => (
  <div
    className={`flex items-center rounded-full border p-0.5 ${
      dark ? "border-[#FAF7F2]/25" : "border-[#241C1A]/15"
    }`}
    role="group"
    aria-label="Lingua / Language"
  >
    {["it", "en"].map((l) => (
      <button
        key={l}
        data-testid={`lang-toggle-${l}`}
        onClick={() => setLang(l)}
        className={`px-3 py-1 rounded-full font-accent text-[11px] uppercase tracking-widest transition-colors duration-300 ${
          lang === l
            ? "bg-[#C86D51] text-[#FAF7F2]"
            : dark
              ? "text-[#FAF7F2]/70 hover:text-[#FAF7F2]"
              : "text-[#6E625E] hover:text-[#241C1A]"
        }`}
      >
        {l}
      </button>
    ))}
  </div>
);

export default function Header({ t, lang, setLang }) {
  const [open, setOpen] = useState(false);

  const LINKS = [
    { href: "#etica", label: t.nav.ethic, testid: "nav-link-etica" },
    { href: "#pistacchio", label: t.nav.pistachio, testid: "nav-link-pistacchio" },
    { href: "#gusti", label: t.nav.flavours, testid: "nav-link-gusti" },
    { href: "#negozi", label: t.nav.shops, testid: "nav-link-negozi" },
    { href: "#contatti", label: t.nav.contacts, testid: "nav-link-contatti" },
  ];

  return (
    <>
      <header
        data-testid="header-nav"
        className="fixed top-0 left-0 right-0 z-50 bg-[#FAF7F2]/85 backdrop-blur-md border-b border-[#241C1A]/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between gap-4">
          <a href="#top" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
            <img src="/images/logo-fiordipanna.png" alt="Fiordipanna dal 1994" className="h-8 sm:h-10 w-auto" />
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testid}
                className="text-sm font-medium tracking-wide text-[#241C1A]/80 hover:text-[#C86D51] transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LangToggle lang={lang} setLang={setLang} />
            <a
              href="#negozi"
              data-testid="header-cta-store"
              className="hidden md:inline-flex rounded-full bg-[#1E1614] text-[#FAF7F2] px-5 py-2.5 text-sm font-medium hover:bg-[#C86D51] transition-colors duration-300"
            >
              {t.nav.findShop}
            </a>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="lg:hidden p-2 -mr-2 text-[#241C1A]"
              aria-label="Apri menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] bg-[#1E1614] text-[#FAF7F2] flex flex-col"
          >
            <div className="flex items-center justify-between px-4 h-16">
              <span className="font-display text-xl italic">Fiordipanna</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="p-2 -mr-2"
                aria-label="Chiudi menu"
              >
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="font-display text-4xl sm:text-5xl py-3 border-b border-[#FAF7F2]/10"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="#negozi"
                data-testid="mobile-cta-store"
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.5 }}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C86D51] px-6 py-4 text-base font-medium w-fit"
              >
                <MapPin size={18} /> {t.nav.mobileFind}
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
